import { FormEventHandler, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConventionalSelect, RadioButton, RadioGroup, validators } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { selectIsPreinstall } from '@app/redux';
import { selectSurveyValues, setSurveyValues } from '@app/redux/disconnect';

import { useHasFiberPreinstall } from '../../hooks/useHasFiberPreinstall';
import { DisconnectPageNavigationProps } from '../../index';
import { DisconnectionReason } from './utils';

const DisconnectReason = ({
  errorHandling,
  mainSurveyOptions,
  otherSurveyOptions,
}: {
  errorHandling: DisconnectPageNavigationProps['errorHandling'];
  mainSurveyOptions: DisconnectionReason[];
  otherSurveyOptions: DisconnectionReason[];
}) => {
  const { formError, setFormError, radioError, setRadioError } = errorHandling;
  const dispatch = useDispatch();
  const { t } = useTranslation('Disconnect');
  const otherOptionValue = t('survey.other');

  const surveyValues = useSelector(selectSurveyValues);
  const hasPreinstall = useSelector(selectIsPreinstall);

  const hasFiberPreinstall = useHasFiberPreinstall();

  const [reason, setReason] = useState<string>('');
  const [showOtherOptions, setShowOtherOptions] = useState(false);

  const formValidationRules = useMemo(() => {
    const validationRules = [];
    validationRules.push(validators.required({ message: t('survey.error.text') }));
    if (validationRules.length && reason) {
      setFormError(undefined);

      return [];
    }
    return validationRules;
  }, [reason]);

  useEffect(() => {
    if (!hasFiberPreinstall) {
      const shouldShowOtherOptions =
        surveyValues.disconnectReason.includes(otherOptionValue) || reason.includes(otherOptionValue);

      setShowOtherOptions(shouldShowOtherOptions);
    }
  }, [reason, hasFiberPreinstall]);

  function getOptionProps(options: DisconnectionReason[]) {
    return options.map((option: DisconnectionReason) => ({
      label: option.value,
      value: option.value,
      id: option.value,
    }));
  }

  const mainOptions = getOptionProps(mainSurveyOptions);
  const otherOptions = getOptionProps(otherSurveyOptions);

  function getSalesforceCode(options: DisconnectionReason[], inputTargetValue: string): string | undefined {
    const found = options.find((option) => option.value === inputTargetValue);
    return found?.salesforceCode;
  }

  const handleChange: FormEventHandler<HTMLInputElement> = (e) => {
    const target = e.target as HTMLInputElement;
    const disconnectReason = target.value;
    const salesforceCode = getSalesforceCode(mainSurveyOptions, disconnectReason);

    setReason(disconnectReason);

    // If the user selects a different option, reset selected other reason too, if applicable
    const otherReason = disconnectReason !== otherOptionValue ? '' : surveyValues.otherReason;

    dispatch(setSurveyValues({ disconnectReason, otherReason, salesforceCode }));
  };

  const handleOtherReasonSelection = (otherReason: string) => {
    const salesforceCode = getSalesforceCode(otherSurveyOptions, otherReason);
    dispatch(setSurveyValues({ otherReason, salesforceCode }));
  };

  return (
    <>
      <ConventionalSelect
        id="main-reasons"
        fluid
        name="main-reasons"
        labelProps={{
          labelText: hasPreinstall
            ? t('survey.satellite.preinstall.reason.label')
            : t('survey.satellite.active.reason.label'),
        }}
        required={true}
        options={mainOptions}
        onChange={handleChange}
        value={surveyValues.disconnectReason}
        validationRules={formValidationRules}
        error={formError}
      />
      {showOtherOptions && (
        <RadioGroup
          id="other-reasons"
          name="other-reasons"
          labelProps={{ labelText: t('survey.otherReasons') }}
          options={otherOptions}
          required={true}
          validationRules={formValidationRules}
          error={radioError}
        >
          {otherOptions.map((option) => {
            const checked = surveyValues.otherReason === option.value;

            if (checked) {
              setRadioError(undefined);
            }

            return (
              <RadioButton
                label={option.label}
                value={option.value}
                key={option.id}
                checked={checked}
                onClick={() => handleOtherReasonSelection(option.value)}
              />
            );
          })}
        </RadioGroup>
      )}
    </>
  );
};

export default DisconnectReason;
